
import { Component, Vue } from 'vue-property-decorator'
import DashboardHero from './components/hero.vue'
import DashboardStatCell from './components/stat-cell.vue'
import DashboardHourlyChart from './components/hourly-chart.vue'
import DashboardTopApiTable from './components/top-api-table.vue'
import DashboardTopPageTable from './components/top-page-table.vue'
import DashboardTopUsersTable from './components/top-users-table.vue'
import DashboardModule1Map from './components/module1-map-table.vue'
import { MOCK_HERO } from '@/constants/monitor-mock'
import { MonitorService } from '@/services/index'
import { IRealtimeCardsData, IStatCard } from '@/types/monitor-types'

/** 6 张卡的固定顺序 / 标签 / 类型 / 跳转目标（label 后端不返回，前端维护） */
const CARD_META: Array<{
    key: keyof IRealtimeCardsData['cards']
    label: string
    kind: 'count' | 'ratio'
    navTarget: string
}> = [
    { key: 'request_total',   label: '请求总量',        kind: 'count', navTarget: '/monitor/api' },
    { key: 'ssr_ratio',       label: 'SSR 占比',        kind: 'ratio', navTarget: '/monitor/api' },
    { key: 'bot_ratio',       label: '爬虫占比',        kind: 'ratio', navTarget: '/monitor/api' },
    { key: 'bot_ssr_count',   label: '爬虫 × SSR 压力', kind: 'count', navTarget: '/monitor/api' },
    { key: 'slow_api_count',  label: '慢接口数',        kind: 'count', navTarget: '/monitor/api' },
    { key: 'violation_users', label: '频率违规用户数',  kind: 'count', navTarget: '/monitor/risk' }
]

@Component({
    name: 'MonitorDashboard',
    components: {
        DashboardHero,
        DashboardStatCell,
        DashboardHourlyChart,
        DashboardTopApiTable,
        DashboardTopPageTable,
        DashboardTopUsersTable,
        DashboardModule1Map
    }
})
export default class MonitorDashboard extends Vue {
    private hero = MOCK_HERO
    /** 指标卡：无数据时全部置零；后端 200 时替换为实时数据 */
    private metrics: IStatCard[] = []

    /** 15s 轮询句柄 */
    private pollTimer: number | null = null

    private created() {
        this.metrics = this.emptyCards()
    }

    private mounted() {
        this.loadCards()
        this.startPoll()
        document.addEventListener('visibilitychange', this.handleVisibility)
    }

    private beforeDestroy() {
        this.stopPoll()
        document.removeEventListener('visibilitychange', this.handleVisibility)
    }

    /** 无数据 → 展示模型（value=0, delta=null, sparkline=[]，焦点关闭） */
    private emptyCards(): IStatCard[] {
        return CARD_META.map(m => ({
            id: m.key,
            label: m.label,
            kind: m.kind,
            value: 0,
            delta: null,
            sparkline: [],
            focus: false,
            navTarget: m.navTarget
        }))
    }

    /** 后端响应 → 展示模型（按 CARD_META 固定顺序） */
    private mapCards(data: IRealtimeCardsData): IStatCard[] {
        return CARD_META.map(m => {
            const c = data.cards[m.key]
            return {
                id: m.key,
                label: m.label,
                kind: m.kind,
                value: c.value,
                delta: c.delta,
                sparkline: c.sparkline,
                focus: !!c.focus,
                navTarget: m.navTarget
            }
        })
    }

    /** 拉取 6 指标卡；非 200 / data 为 null → 全部置零（无数据兜底，不显示假数） */
    private async loadCards() {
        try {
            const res = await MonitorService.getRealtimeCards()
            if (res.status === 200 && res.data) {
                this.metrics = this.mapCards(res.data as IRealtimeCardsData)
            } else {
                // 后端明确说没数据（业务异常 / data=null）→ 6 卡全部归零
                this.metrics = this.emptyCards()
            }
        } catch (e) {
            // 网络异常由 xhr/common-handler 统一提示；此处保留当前值，避免网络抖动导致闪烁
        }
    }

    /** 15s 轮询 */
    private startPoll() {
        this.stopPoll()
        this.pollTimer = window.setInterval(() => {
            this.loadCards()
        }, 15000)
    }

    private stopPoll() {
        if (this.pollTimer !== null) {
            window.clearInterval(this.pollTimer)
            this.pollTimer = null
        }
    }

    /** 页面不可见时暂停轮询；回到可见立即拉一次并恢复 */
    private handleVisibility = () => {
        if (document.hidden) {
            this.stopPoll()
        } else {
            this.loadCards()
            this.startPoll()
        }
    }
}
